/**
 * CircularProgressExamples.tsx
 * Comprehensive examples of HeroUI CircularProgress component
 * 
 * Showcases all HeroUI CircularProgress features using our shared components
 */

import React, { useState, useEffect } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { CircularProgress } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';


export const CircularProgressExamples: React.FC = () => {
  const [dynamicValue, setDynamicValue] = useState(0);
  const [tradingProgress, setTradingProgress] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  
  // Simulate dynamic progress
  useEffect(() => {
    const interval = setInterval(() => {
      setDynamicValue(prev => prev >= 100 ? 0 : prev + 2);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const simulateTrading = () => {
    setTradingProgress(0);
    const interval = setInterval(() => {
      setTradingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + Math.random() * 10;
      });
    }, 300);
  };

  const simulateAnalysis = () => {
    setAnalysisProgress(0);
    const interval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + Math.random() * 8;
      });
    }, 200);
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress value={65} />
                  <h4 className="text-sm font-medium text-text-secondary">Default Progress</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress value={75} label="Loading..." />
                  <h4 className="text-sm font-medium text-text-secondary">With Label</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress value={45} showValueLabel={false} />
                  <h4 className="text-sm font-medium text-text-secondary">Without Value Label</h4>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Sizes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Sizes</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress size="sm" value={40} label="Small" />
                  <h4 className="text-sm font-medium text-text-secondary">Small</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress size="md" value={60} label="Medium" />
                  <h4 className="text-sm font-medium text-text-secondary">Medium (Default)</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress size="lg" value={80} label="Large" />
                  <h4 className="text-sm font-medium text-text-secondary">Large</h4>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Colors</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress color="default" value={35} />
                  <h4 className="text-sm font-medium text-text-secondary">Default</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress color="primary" value={50} />
                  <h4 className="text-sm font-medium text-text-secondary">Primary</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress color="secondary" value={65} />
                  <h4 className="text-sm font-medium text-text-secondary">Secondary</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress color="success" value={80} />
                  <h4 className="text-sm font-medium text-text-secondary">Success</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress color="warning" value={45} />
                  <h4 className="text-sm font-medium text-text-secondary">Warning</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress color="danger" value={25} />
                  <h4 className="text-sm font-medium text-text-secondary">Danger</h4>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* States & Stroke Width */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">States & Stroke Width</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Indeterminate */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Indeterminate</h4>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col items-center space-y-3">
                    <CircularProgress isIndeterminate color="primary" label="Loading..." />
                    <h4 className="text-sm font-medium text-text-secondary">With Label</h4>
                  </div>
                  
                  <div className="flex flex-col items-center space-y-3">
                    <CircularProgress isIndeterminate color="secondary" />
                    <h4 className="text-sm font-medium text-text-secondary">Without Label</h4>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Custom Stroke Width */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Stroke Width</h4>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col items-center space-y-3">
                    <CircularProgress value={70} strokeWidth={2} color="primary" />
                    <h4 className="text-sm font-medium text-text-secondary">Thin (2px)</h4>
                  </div>
                  
                  <div className="flex flex-col items-center space-y-3">
                    <CircularProgress value={70} strokeWidth={6} color="success" />
                    <h4 className="text-sm font-medium text-text-secondary">Thick (6px)</h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Dynamic Progress */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Dynamic Progress</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress value={dynamicValue} color="primary" label="Auto" />
                  <h4 className="text-sm font-medium text-text-secondary">Auto-incrementing</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress 
                    value={tradingProgress} 
                    color="success" 
                    label="Trading"
                    showValueLabel={tradingProgress > 0}
                  />
                  <Button 
                    onPress={simulateTrading}
                    color="success"
                    size="sm"
                    isDisabled={tradingProgress > 0 && tradingProgress < 100}
                  >
                    {tradingProgress > 0 && tradingProgress < 100 ? 'Trading...' : 'Start Trading'}
                  </Button>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress 
                    value={analysisProgress} 
                    color="warning" 
                    label="Analysis"
                    showValueLabel={analysisProgress > 0}
                  />
                  <Button 
                    onPress={simulateAnalysis}
                    color="warning"
                    size="sm"
                    isDisabled={analysisProgress > 0 && analysisProgress < 100}
                  >
                    {analysisProgress > 0 && analysisProgress < 100 ? 'Analyzing...' : 'Start Analysis'}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Custom Formatting */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Custom Formatting</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress 
                    value={0.75} 
                    formatOptions={{ style: 'percent' }}
                    color="primary"
                    label="Completion"
                  />
                  <h4 className="text-sm font-medium text-text-secondary">Percentage Format</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress 
                    value={42} 
                    minValue={0}
                    maxValue={100}
                    color="secondary"
                    label="42 / 100 trades"
                    showValueLabel={false}
                  />
                  <h4 className="text-sm font-medium text-text-secondary">Custom Units</h4>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress 
                    value={67.89} 
                    formatOptions={{ maximumFractionDigits: 1 }}
                    color="success"
                    label="Accuracy"
                  />
                  <h4 className="text-sm font-medium text-text-secondary">Decimal Places</h4>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Trading Context Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Context</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Daily Goal</h4>
              </CardHeader>
              <CardBody>
                <div className="flex flex-col items-center space-y-4">
                  <CircularProgress 
                    value={78} 
                    color="success" 
                    size="lg"
                    strokeWidth={6}
                    label="$1,560"
                    showValueLabel={false}
                  />
                  <div className="text-center">
                    <div className="text-2xl font-bold text-success">$1,560</div>
                    <div className="text-sm text-text-secondary">of $2,000 goal</div>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Risk Usage</h4>
              </CardHeader>
              <CardBody>
                <div className="flex flex-col items-center space-y-4">
                  <CircularProgress 
                    value={65} 
                    color="warning" 
                    size="lg"
                    strokeWidth={6}
                    showValueLabel={false}
                  />
                  <div className="text-center">
                    <div className="text-2xl font-bold text-warning">65%</div>
                    <div className="text-sm text-text-secondary">of daily risk</div>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Win Rate</h4>
              </CardHeader>
              <CardBody>
                <div className="flex flex-col items-center space-y-4">
                  <CircularProgress 
                    value={82} 
                    color="primary" 
                    size="lg"
                    strokeWidth={6}
                    showValueLabel={false}
                  />
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">82%</div>
                    <div className="text-sm text-text-secondary">win rate today</div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Loading States Dashboard */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">System Status Dashboard</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress isIndeterminate color="primary" size="sm" />
                  <div className="text-center">
                    <div className="text-sm font-medium text-text-primary">Market Data</div>
                    <div className="text-xs text-text-secondary">Syncing...</div>
                  </div>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress value={100} color="success" size="sm" />
                  <div className="text-center">
                    <div className="text-sm font-medium text-text-primary">Portfolio</div>
                    <div className="text-xs text-success">Connected</div>
                  </div>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress value={67} color="warning" size="sm" />
                  <div className="text-center">
                    <div className="text-sm font-medium text-text-primary">Backtest</div>
                    <div className="text-xs text-warning">Processing...</div>
                  </div>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <CircularProgress value={0} color="danger" size="sm" />
                  <div className="text-center">
                    <div className="text-sm font-medium text-text-primary">Reports</div>
                    <div className="text-xs text-danger">Failed</div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Performance Metrics */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Performance Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Monthly Progress</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-text-primary">Profit Target</div>
                      <div className="text-sm text-text-secondary">$15,000 / $20,000</div>
                    </div>
                    <CircularProgress value={75} color="success" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-text-primary">Risk Management</div>
                      <div className="text-sm text-text-secondary">Within limits</div>
                    </div>
                    <CircularProgress value={45} color="primary" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-text-primary">Trade Execution</div>
                      <div className="text-sm text-text-secondary">Consistency score</div>
                    </div>
                    <CircularProgress value={90} color="secondary" />
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Strategy Analysis</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-text-primary">Trend Following</div>
                      <div className="text-sm text-text-secondary">85% accuracy</div>
                    </div>
                    <CircularProgress value={85} color="success" strokeWidth={3} />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-text-primary">Mean Reversion</div>
                      <div className="text-sm text-text-secondary">62% accuracy</div>
                    </div>
                    <CircularProgress value={62} color="warning" strokeWidth={3} />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-text-primary">Breakout Trading</div>
                      <div className="text-sm text-text-secondary">78% accuracy</div>
                    </div>
                    <CircularProgress value={78} color="primary" strokeWidth={3} />
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};